import {
    HordeAuthorityDocumentSchema,
    HordeCommitDocumentSchema,
    HordeGameEventDocumentSchema,
    HordePlayerSecretViewDocumentSchema,
    PersistedHordeGameSchema,
    hordePlayerSecretViewDocumentId,
    type HordeIntent,
    type PersistedHordeGame,
    type PlayerSecretViewState,
} from '@animus/type/domain';
import {
    HordeCommitError,
    type HordeAuthorityTransition,
    type HordeCommitOutcome,
} from '@animus/utility/web/persistence';
import type { RxCollection } from 'rxdb/plugins/core';
import type { AnimusDatabase } from './collections';
import { createPlayerSecretViews, toAuthorityState } from './horde-session-projections';
import { withoutRxdbMetadata } from './rxdb-document.util';
import { isQuotaExceededError } from './rxdb-error.util';

/** The typed Horde database: every collection the commit path reads/writes is known and signal-reactive. */
type HordeDatabase = AnimusDatabase;

/**
 * The durable write half of the Horde authority protocol: the commit journal that makes an interrupted commit
 * replayable, plus the derived projections a commit fans out to.
 *
 * These take the database explicitly rather than resolving it themselves, so the adapter owns storage lifetime and
 * cross-tab leasing while every write on the commit path stays reachable by a fault-injecting test database.
 */

/**
 * Applies one authoritative transition, assuming the caller already holds the per-game authority lease.
 *
 * The journal entry is written before the transition and cleared after projections, so a crash anywhere in between
 * leaves a record `reconcileIncompleteCommits` can replay.
 */
export async function commitAuthorityTransition({
    db,
    intent,
    transition,
}: {
    db: HordeDatabase;
    intent: HordeIntent;
    transition: HordeAuthorityTransition;
}): Promise<HordeCommitOutcome> {
    await reconcileIncompleteCommits(db, intent.metadata.gameId);
    const authorityDoc = await db['hordeAuthorities'].findOne(intent.metadata.gameId).exec();
    const initial = toAuthorityState(authorityDoc);

    if (!authorityDoc || !initial) {
        throw new Error(`No host authority exists for game ${intent.metadata.gameId}.`);
    }

    if (initial.game.host.deviceId !== intent.metadata.committedByHostDeviceId) {
        throw new Error('The committing device is not the immutable game host.');
    }

    const commit = HordeCommitDocumentSchema.parse({
        authorityRevision: null,
        commitId: intent.metadata.eventId,
        gameId: intent.metadata.gameId,
        intent,
        status: 'prepared',
        updatedAt: intent.metadata.createdAt,
    });

    await db['hordeCommits'].incrementalUpsert(commit).catch((error: unknown) => {
        const message = isQuotaExceededError(error)
            ? 'Storage is full - the commit journal could not be written.'
            : 'The commit journal could not be prepared.';

        throw new HordeCommitError(message, 'notCommitted', {
            cause: error,
        });
    });

    let acceptedByThisCall = false;
    const committedDoc = await authorityDoc
        .incrementalModify((document: unknown) => {
            const current = HordeAuthorityDocumentSchema.parse(withoutRxdbMetadata(document));

            if (current.game.acceptedEventIds.includes(intent.metadata.eventId)) {
                return current;
            }

            const next = transition(
                {
                    game: current.game,
                    vault: current.vault,
                },
                intent
            );

            acceptedByThisCall = true;

            return HordeAuthorityDocumentSchema.parse({
                game: next.game,
                gameId: next.game.gameId,
                vault: next.vault,
            });
        })
        .catch((error: unknown) => {
            const message =
                error instanceof Error
                    ? `The authoritative transition was not committed: ${error.message}`
                    : 'The authoritative transition was not committed.';

            throw new HordeCommitError(message, 'notCommitted', { cause: error });
        });
    const committed = HordeAuthorityDocumentSchema.parse(withoutRxdbMetadata(committedDoc.toMutableJSON()));
    const playerSecretViews = createPlayerSecretViews(committed.vault);

    try {
        const commitDoc = await db['hordeCommits'].findOne(intent.metadata.eventId).exec();

        await commitDoc?.incrementalPatch({
            authorityRevision: committed.game.revision,
            status: 'projectionPending',
            updatedAt: new Date().toISOString(),
        });
        await finishCommit(db, { game: committed.game, intent, playerSecretViews });
    } catch (error: unknown) {
        throw new HordeCommitError(
            'Authority committed; derived projections are pending recovery.',
            'committedProjectionPending',
            { cause: error }
        );
    }

    // Best-effort, never on the hot failure path: a fully-projected commit no longer needs its journal entry for
    // `reconcileIncompleteCommits`, so trimming here (instead of before the throwing paths above) keeps storage
    // bounded without risking a pruning failure masquerading as a commit failure.
    //
    // The in-flight guard collapses overlapping prunes for the same game into a no-op, and the function already
    // swallows/logs its own errors.
    await pruneJournalHistory(db, intent.metadata.gameId);

    return {
        duplicate: !acceptedByThisCall,
        game: committed.game,
        playerSecretViews,
        revision: committed.game.revision,
        status: 'fullyProjected',
    };
}

/**
 * Completes projections for authority revisions that survived an interrupted commit.
 *
 * A prepared record whose event is absent from authority was never committed and is intentionally left for the
 * normal idempotent retry path. Once authority contains the event, projection writes can always be replayed.
 */
export async function reconcileIncompleteCommits(db: HordeDatabase, gameId: string): Promise<void> {
    const documents = await db['hordeCommits']
        .find({
            selector: {
                gameId,
                status: { $ne: 'projected' },
            },
        })
        .exec();
    const authority = toAuthorityState(await db['hordeAuthorities'].findOne(gameId).exec());

    if (!authority) {
        return;
    }

    for (const document of documents) {
        const parsed = HordeCommitDocumentSchema.safeParse(document.toMutableJSON());

        if (!parsed.success || !authority.game.acceptedEventIds.includes(parsed.data.intent.metadata.eventId)) {
            continue;
        }

        await finishCommit(db, {
            game: authority.game,
            intent: parsed.data.intent,
            playerSecretViews: createPlayerSecretViews(authority.vault),
        });
    }
}

/** Commits public, host-vault, and player-secret projections for one revision. */
export async function writeProjections({
    db,
    game,
    playerSecretViews,
}: {
    db: HordeDatabase;
    game: PersistedHordeGame;
    playerSecretViews: PlayerSecretViewState[];
}): Promise<void> {
    await db['hordeSessions'].incrementalUpsert(PersistedHordeGameSchema.parse(game));
    await Promise.all(
        playerSecretViews.map((view) =>
            db['hordePlayerSecretViews'].incrementalUpsert(
                HordePlayerSecretViewDocumentSchema.parse({
                    ...view,
                    documentId: hordePlayerSecretViewDocumentId(view.gameId, view.playerId),
                })
            )
        )
    );
}

/** Idempotently writes every derived record and marks the durable commit fully projected last. */
async function finishCommit(
    db: HordeDatabase,
    {
        game,
        intent,
        playerSecretViews,
    }: {
        game: PersistedHordeGame;
        intent: HordeIntent;
        playerSecretViews: PlayerSecretViewState[];
    }
): Promise<void> {
    await writeProjections({ db, game, playerSecretViews });
    await writeAcceptedEvent({ db, game, intent });
    await acceptQueuedIntent({ db, intent, revision: game.revision });

    const commitDoc = await db['hordeCommits'].findOne(intent.metadata.eventId).exec();

    await commitDoc?.incrementalPatch({
        authorityRevision: game.revision,
        status: 'projected',
        updatedAt: new Date().toISOString(),
    });
}

/** Persists the accepted event record for an authoritative intent. */
async function writeAcceptedEvent({
    db,
    game,
    intent,
}: {
    db: HordeDatabase;
    game: PersistedHordeGame;
    intent: HordeIntent;
}): Promise<void> {
    const accepted = game.historySummary.find((entry) => entry.eventId === intent.metadata.eventId);

    if (!accepted) {
        throw new Error(`Committed event ${intent.metadata.eventId} has no audit entry.`);
    }

    await db['hordeGameEvents'].incrementalUpsert(
        HordeGameEventDocumentSchema.parse({
            ...accepted,
            actorDeviceId: intent.metadata.actorDeviceId,
            gameId: game.gameId,
            privatePlayerId:
                typeof intent.metadata.visibility === 'string'
                    ? null
                    : intent.metadata.visibility.privateToPlayerId,
            visibility: typeof intent.metadata.visibility === 'string' ? 'public' : 'privatePlayer',
        })
    );
}

/** Marks a queued intent accepted after its authoritative transition commits. */
async function acceptQueuedIntent({
    db,
    intent,
    revision,
}: {
    db: HordeDatabase;
    intent: HordeIntent;
    revision: number;
}): Promise<void> {
    const doc = await db['hordeGameIntents'].findOne(intent.metadata.eventId).exec();

    if (!doc) {
        return;
    }

    await doc.incrementalPatch({
        acceptedRevision: revision,
        rejectionReason: null,
        status: 'accepted',
        updatedAt: intent.metadata.createdAt,
    });
}

/**
 * Retention caps for the two collections that otherwise gain one permanent document per accepted intent, with no
 * other pruning path anywhere in the codebase (see the module doc). Neither collection is read by any UI feature
 * directly - the History tab renders `PersistedHordeGame.historySummary`, which the domain schema already caps at
 * 512 entries (`libs/type/domain/src/lib/horde-session-game.ts`) - so both are safe to trim well past that cap.
 * `hordeGameEvents` additionally feeds WebRTC replication to (re)connecting peers via checkpoint-based delta sync,
 * not a full-history requirement, so it keeps a larger cushion than `hordeCommits`.
 */
const COMMIT_JOURNAL_RETENTION_LIMIT = 1024;
const GAME_EVENT_RETENTION_LIMIT = 2048;

/**
 * Games with a prune already running for them, keyed by `gameId`. A rapid batch of commits for the same game (e.g.
 * a bulk "Resolve at the table" acceptance) each fire a detached prune off `commitAuthorityTransition`; without this
 * guard every one of them would run its own full-collection scan concurrently. Module-level and process-lifetime
 * scoped (never cleared on error) is fine here - the `finally` below always removes the entry once the in-flight
 * prune settles, success or failure.
 */
const gamesWithPruneInFlight = new Set<string>();

/**
 * Opportunistically trims fully-projected journal and event history for one game so storage stays bounded even on
 * IndexedDB's much larger quota. Best-effort: pruning failures are logged and swallowed, never surfaced as a
 * commit failure, since this only ever runs after a commit has already fully succeeded.
 *
 * No-ops if a prune for this `gameId` is already in flight rather than piling up a second concurrent scan - the
 * in-flight prune already covers any document written before it started, and the caller never awaits this anyway
 * (see `commitAuthorityTransition`).
 */
async function pruneJournalHistory(db: HordeDatabase, gameId: string): Promise<void> {
    if (gamesWithPruneInFlight.has(gameId)) {
        return;
    }

    gamesWithPruneInFlight.add(gameId);

    try {
        await pruneStaleDocuments({
            collection: db['hordeCommits'],
            retain: COMMIT_JOURNAL_RETENTION_LIMIT,
            selector: { gameId, status: 'projected' },
            sortField: 'updatedAt',
        });
        await pruneStaleDocuments({
            collection: db['hordeGameEvents'],
            retain: GAME_EVENT_RETENTION_LIMIT,
            selector: { gameId },
            sortField: 'revision',
        });
    } catch (error: unknown) {
        console.error(
            '[HordeCommitJournal] Journal pruning failed (non-fatal, storage may grow unbounded).',
            error
        );
    } finally {
        gamesWithPruneInFlight.delete(gameId);
    }
}

/**
 * Deletes every document in `selector` beyond the newest `retain` (ordered descending by `sortField`).
 *
 * The selector/sort are loose because this helper is only called with the journal/event collections and
 * caller-validated fields; the strict `MangoQuery` generics would reject the shared `gameId` selector across the
 * two different document shapes.
 */
async function pruneStaleDocuments({
    collection,
    retain,
    selector,
    sortField,
}: {
    collection: RxCollection;
    retain: number;
    selector: Record<string, unknown>;
    sortField: string;
}): Promise<void> {
    const documents = await collection
        .find({
            selector,
            sort: [{ [sortField]: 'desc' }],
        })
        .exec();
    const stale = documents.slice(retain);

    if (stale.length === 0) {
        return;
    }

    await collection.bulkRemove(stale.map((document) => document.primary));
}
